import useCheckPC from "@repo/hooks/utils/useCheckPC";
import classNames from "classnames";
import type { ButtonHTMLAttributes } from "react";
import React, { Children, isValidElement, useMemo } from "react";

import LoadingIcon from "@atoms/LoadingIcon";

interface IProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "contained" | "text" | "icon" | "containedIcon" | "social";
  styleType?: "primary" | "secondary" | "tertiary";
  className?: string;
  children?: React.ReactNode;
  isLoading?: boolean;
}

function Button({
  variant = "contained",
  styleType = "primary",
  type = "button",
  isLoading,
  className,
  children,
  ...props
}: IProps) {
  const { isPC } = useCheckPC();

  const isSingleLabel = useMemo(() => Children.count(children) === 1, [children]);
  const isDoubleLabel = useMemo(
    () => Children.toArray(children).every((child) => isValidElement(child) && child.type === "span"),
    [children],
  );

  return (
    <button
      className={classNames(
        "flex! items-center active:scale-95 disabled:cursor-not-allowed disabled:opacity-30 disabled:active:scale-100",
        isLoading && "pointer-events-none relative",
        variant === "contained" && {
          "rounded-md": true,
          "text-btn-primary": styleType === "primary",
          "text-btn-secondary": styleType === "secondary" || styleType === "tertiary",
          "bg-btn-primary": styleType === "primary",
          "bg-btn-secondary": styleType === "secondary",
          "bg-btn-tertiary": styleType === "tertiary",
          "disabled:opacity-50": styleType !== "tertiary",
          "py-1": isPC, // 4px
          "py-2": !isPC, // 8px
          "px-3": !isSingleLabel, // 12px
          "px-6": isSingleLabel, // 24px
          "gap-2.5": !isDoubleLabel, // 10px
          "h-7": isPC,
          "h-9": !isPC, // 36px
        },
        variant === "text" && {
          "text-primary": styleType === "primary",
          "disabled:text-secondary": true,
          "gap-1": true, // 4px
        },
        variant === "icon" && {
          "text-secondary": styleType === "secondary",
          "p-0": true, // 0px
        },
        variant === "containedIcon" && {
          "text-btn-primary": styleType === "primary",
          "text-btn-secondary": styleType === "secondary",
          "bg-btn-primary": styleType === "primary",
          "bg-btn-secondary": styleType === "secondary",
          "justify-center": true,
          "px-3": true,
          "py-2": true,
          "rounded-md": true,
        },
        variant === "social" && {
          "text-btn-primary": styleType === "primary",
          "text-btn-secondary": styleType === "secondary",
          "bg-btn-primary": styleType === "primary",
          "bg-btn-secondary": styleType === "secondary",
          "justify-center": true,
          "rounded-md": true,
          "py-[0.625rem]": true,
        },
        className,
      )}
      type={type}
      {...props}
    >
      {isLoading ? <LoadingIcon className="center absolute" type="spinner" /> : null}
      <div
        className={classNames(
          isLoading ? "invisible" : "visible flex w-full items-center justify-center gap-2.5 whitespace-nowrap",
          isDoubleLabel && "flex w-full justify-between! gap-2.5",
        )}
      >
        {children}
      </div>
    </button>
  );
}

export default Button;
